import * as React from 'react';
import { buildStableItemIds, formatApiDateCompact, getGcpItemAmount, isGcpRateFullySet } from '../InvoicingUtils';
import { ScanCostWithDateType } from '../types';
import { useGcpItemPrices } from './useGcpItemPrices';

export type TimeMaterialsDatePrices = { flightsCosts: string; mileage: string };

export type GcpTimeMaterialsItem = ScanCostWithDateType & { id: string };

export type ScanTimeMaterialsDateGroup = {
  dateKey: string;
  displayDate: string;
  scanPanoLidarItems: ScanCostWithDateType[];
  gcpItems: GcpTimeMaterialsItem[];
  gcpItemsTotal: number;
};

export function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
    2,
    '0',
  )}`;
}

type UseTimeMaterialsDateGroupsArgs = {
  isFixedFee: boolean;
  filteredCosts: ScanCostWithDateType[] | null;
};

export function useTimeMaterialsDateGroups({ isFixedFee, filteredCosts }: UseTimeMaterialsDateGroupsArgs) {
  const [timeMaterialsDatePrices, setTimeMaterialsDatePrices] = React.useState<Record<string, TimeMaterialsDatePrices>>(
    {},
  );

  const rawGroups = React.useMemo((): Omit<ScanTimeMaterialsDateGroup, 'gcpItemsTotal'>[] => {
    if (isFixedFee || !filteredCosts) return [];
    const byDate = new Map<string, ScanCostWithDateType[]>();
    for (const item of filteredCosts) {
      const key = toDateKey(item.flightDate);
      const list = byDate.get(key) ?? [];
      list.push(item);
      byDate.set(key, list);
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, items]) => {
        const gcpMissions = items.filter((i) => i.category === 'gcp_services');
        // Keyed by record id, not by position — the selected period re-derives these groups and the
        // per-item price/materials state has to stay on its own mission. See buildStableItemIds.
        const gcpIds = buildStableItemIds(
          dateKey,
          gcpMissions.map((i) => i.airtableRecordId),
        );

        return {
          dateKey,
          displayDate: formatApiDateCompact(items[0].flightDate),
          // Exclusion-based (not a fixed scan/pano/lidar allowlist) so any non-GCP category —
          // including video/additional-images — still shows up and gets billed, matching
          // InvoicingUtils.ts's buildFixDateGroups.
          scanPanoLidarItems: items.filter((i) => i.category !== 'gcp_services'),
          gcpItems: gcpMissions.map((item, idx) => ({ ...item, id: gcpIds[idx] })),
        };
      });
  }, [isFixedFee, filteredCosts]);

  // GCP services are billable regardless of unprocessed state (they never get an "in
  // review" distinction elsewhere in this UI), so only failed items are priceable —
  // matching the equivalent selection in InvoicingUtils.ts's buildFixDateGroups.
  const gcpPriceableItems = React.useMemo(
    () =>
      rawGroups
        .flatMap((g) => g.gcpItems)
        .filter((i) => !i.isFailed)
        .map((item) => ({
          id: item.id,
          defaultAmount: getGcpItemAmount(
            item.cost,
            item.consultantGcpServiceHourlyRate,
            item.consultantGcpServiceTimeInHours,
          ),
          // Rate × hours rows are read-only here too (TimeMaterialsCostBlock renders no input for
          // them), so they bill the calculated amount and never an override.
          locked: isGcpRateFullySet(item.consultantGcpServiceHourlyRate, item.consultantGcpServiceTimeInHours),
        })),
    [rawGroups],
  );

  const gcpDefaultAmountById = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const item of gcpPriceableItems) map.set(item.id, item.defaultAmount);
    return map;
  }, [gcpPriceableItems]);

  const {
    prices: gcpItemPrices,
    updatePrice: updateGcpItemPrice,
    getPrice: getGcpItemPrice,
    isDirty: isGcpItemPricesDirty,
  } = useGcpItemPrices(gcpPriceableItems);

  const timeMaterialsDateGroups = React.useMemo(
    (): ScanTimeMaterialsDateGroup[] =>
      rawGroups.map((group) => ({
        ...group,
        gcpItemsTotal: group.gcpItems
          .filter((i) => !i.isFailed)
          .reduce((s, item) => s + getGcpItemPrice(item.id, gcpDefaultAmountById.get(item.id) ?? 0), 0),
      })),
    [rawGroups, getGcpItemPrice, gcpDefaultAmountById],
  );

  const timeMaterialsTotal = React.useMemo(
    () =>
      isFixedFee
        ? 0
        : timeMaterialsDateGroups.reduce((s, group) => {
            const prices = timeMaterialsDatePrices[group.dateKey];
            return (
              s + parseFloat(prices?.flightsCosts || '0') + parseFloat(prices?.mileage || '0') + group.gcpItemsTotal
            );
          }, 0),
    [isFixedFee, timeMaterialsDateGroups, timeMaterialsDatePrices],
  );

  const setDatePrice = React.useCallback((dateKey: string, field: keyof TimeMaterialsDatePrices, val: string) => {
    setTimeMaterialsDatePrices((prev) => {
      const existing = prev[dateKey] ?? { flightsCosts: '', mileage: '' };
      return { ...prev, [dateKey]: { ...existing, [field]: val } };
    });
  }, []);

  return {
    timeMaterialsDatePrices,
    timeMaterialsDateGroups,
    timeMaterialsTotal,
    setDatePrice,
    gcpItemPrices,
    updateGcpItemPrice,
    getGcpItemPrice,
    isGcpItemPricesDirty,
    gcpDefaultAmountById,
  };
}
